import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { resetGlossaryCache } from "@/lib/kbContentRuntime";

const blank = { term: "", definition: "", aliases: [], link_slug: "" };

export default function KBGlossary() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [q, setQ] = usePersistedState("kb-glossary.q", "");
  const [editing, setEditing] = useState(null);   // term object or blank
  const [form, setForm] = useState(blank);
  const [aliasDraft, setAliasDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/kb/glossary")
      .then((r) => { setTerms(r.data.terms || []); setCanEdit(!!r.data.can_edit); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const open = (row) => {
    setEditing(row || blank);
    setForm(row ? { ...row } : blank);
    setAliasDraft("");
  };

  const save = async () => {
    if (!form.term.trim() || !form.definition.trim()) {
      toast.error("A term and its definition are both required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        term: form.term, definition: form.definition,
        aliases: form.aliases || [], link_slug: form.link_slug || "",
      };
      if (editing?.term_id) await api.patch(`/kb/glossary/${editing.term_id}`, body);
      else await api.post("/kb/glossary", body);
      toast.success(editing?.term_id ? "Term updated" : "Term added");
      resetGlossaryCache();
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove “${row.term}” from the glossary?`)) return;
    try {
      await api.delete(`/kb/glossary/${row.term_id}`);
      toast.success("Term removed");
      resetGlossaryCache();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAlias = () => {
    const v = aliasDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, aliases: [...new Set([...(f.aliases || []), v])].slice(0, 8) }));
    setAliasDraft("");
  };

  const needle = q.trim().toLowerCase();
  const rows = needle
    ? terms.filter((t) => t.term.toLowerCase().includes(needle)
      || t.definition.toLowerCase().includes(needle)
      || (t.aliases || []).some((a) => a.toLowerCase().includes(needle)))
    : terms;

  return (
    <div data-testid="kb-glossary-page">
      <PageHeader title="Glossary" testid="kb-glossary-header"
        subtitle="Define a term once — every article underlines it and shows the definition on hover">
        {canEdit && (
          <Button size="sm" data-testid="kb-glossary-new" onClick={() => open(null)}
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1 h-4 w-4" /> New term
          </Button>
        )}
      </PageHeader>

      <Card className="border-slate-200 dark:border-slate-800 p-4">
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input data-testid="kb-glossary-search" value={q} placeholder="Search terms…"
            onChange={(e) => setQ(e.target.value)} className="pl-8" />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center" data-testid="kb-glossary-empty">
            <BookOpen className="mx-auto h-8 w-8 text-slate-200" />
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              {terms.length ? "No terms match that search." : "No glossary terms yet."}
            </p>
            {canEdit && !terms.length && (
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                Add one and it will explain itself everywhere it appears in the Knowledge Base.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  <th className="py-2 pr-3 font-semibold">Term</th>
                  <th className="py-2 pr-3 font-semibold">Definition</th>
                  <th className="py-2 pr-3 font-semibold">Also matches</th>
                  <th className="py-2 pr-3 font-semibold">Updated</th>
                  {canEdit && <th className="py-2 text-right font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.term_id} data-testid={`kb-glossary-row-${t.term_id}`}
                    className="border-b border-slate-50 dark:border-slate-800 align-top hover:bg-slate-50/60">
                    <td className="py-2.5 pr-3 font-medium text-slate-800 dark:text-slate-100">{t.term}</td>
                    <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{t.definition}</td>
                    <td className="py-2.5 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {(t.aliases || []).map((a) => (
                          <span key={a} className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-300">{a}</span>
                        ))}
                        {!(t.aliases || []).length && <span className="text-xs text-slate-300 dark:text-slate-600">—</span>}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">
                      {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}
                      {t.updated_by ? <div className="text-slate-400 dark:text-slate-500">{t.updated_by}</div> : null}
                    </td>
                    {canEdit && (
                      <td className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2" title="Edit"
                            data-testid={`kb-glossary-edit-${t.term_id}`} onClick={() => open(t)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-500 hover:bg-rose-50"
                            title="Remove" data-testid={`kb-glossary-delete-${t.term_id}`} onClick={() => remove(t)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="kb-glossary-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.term_id ? "Edit term" : "New glossary term"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Term</Label>
              <Input data-testid="kb-glossary-term-input" value={form.term} maxLength={80}
                placeholder="e.g. SIP trunk"
                onChange={(e) => setForm((f) => ({ ...f, term: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Definition</Label>
              <textarea data-testid="kb-glossary-definition-input" value={form.definition} rows={3}
                maxLength={600} placeholder="One or two plain-English sentences readers will see on hover"
                onChange={(e) => setForm((f) => ({ ...f, definition: e.target.value }))}
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
              <p className="text-[11px] text-slate-400 dark:text-slate-500">{(form.definition || "").length}/600</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Also matches <span className="font-normal text-slate-400 dark:text-slate-500">(abbreviations, plurals)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {(form.aliases || []).map((a) => (
                  <span key={a} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-xs text-slate-700 dark:text-slate-200"
                    data-testid={`kb-glossary-alias-${a}`}>
                    {a}
                    <button type="button" className="text-slate-400 dark:text-slate-500 hover:text-rose-500"
                      onClick={() => setForm((f) => ({ ...f, aliases: f.aliases.filter((x) => x !== a) }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Input data-testid="kb-glossary-alias-input" value={aliasDraft} placeholder="Add an alias"
                  onChange={(e) => setAliasDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                  className="h-8 text-xs" />
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs"
                  data-testid="kb-glossary-alias-add" onClick={addAlias}>Add</Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Read-more article slug <span className="font-normal text-slate-400 dark:text-slate-500">(optional)</span></Label>
              <Input data-testid="kb-glossary-link-input" value={form.link_slug || ""}
                placeholder="sip-trunk-overview"
                onChange={(e) => setForm((f) => ({ ...f, link_slug: e.target.value }))}
                className="font-mono text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}
              data-testid="kb-glossary-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="kb-glossary-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
